<template>
  <v-container fluid class="mt-n6" id="tract-harvest">
    <v-row no-gutters align="center" justify="end" :class="mobileCompatible ? 'mt-2' : ''">
      <v-col v-if="(parseFloat(distanceFromTract) > 0.5 && !distanceCleared) || asOfDate && !dateFilterCleared">
        <v-row dense justify="end" align="center">
          <v-col cols="auto" v-if="asOfDate && !dateFilterCleared">
            <v-chip close @click:close="clearDateFilter" color="info" class="my-n1">
              <span>{{$t('showingDataAsOf')}}: {{utcToLocalDate(asOfDate)}}</span>
            </v-chip>
          </v-col>
          <v-col cols="auto" v-if="parseFloat(distanceFromTract) > 0.5 && !distanceCleared">
            <v-chip close @click:close="clearDistance" color="info" class="my-n1">
              <span>{{$t('youAreMilesFromThisTract', {distance: this.distanceFromTract})}}</span>
            </v-chip>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="auto">
        <v-row
          justify="end"
          align="center"
          class="ml-4"
          dense
          v-show="!mobileCompatible">
          <v-col cols="auto">
            <TabbedColumnAdjuster
            :propColumnSet="harvestHeaders"
            :propTabs="columnAdjusterTabs"
            :isOpen="columnAdjusterOpen"
            :mobileCompatible="mobileCompatible"
            :appendElementId="'tract-detail-harvest'"
            @closed="columnAdjusterOpen = false"
            @new-columns-selected="newColumnsSelected"
            v-show="!mobileCompatible"/>
          </v-col>
          <v-col v-if="isTons && !mobileCompatible" cols="auto">
            <Icon
            :small="false"
            :icon="tableView ? 'mdi-graph' : 'mdi-table'"
            iconColor="primary"
            elementId="tract-harvest-toggle-chart-view"
            :tooltipText="tableView ? $t('viewCharts') : $t('viewTable')"
            @icon-clicked="toggleTableView"
            />
          </v-col>
          <v-col cols="auto" v-if="!mobileCompatible && !depletes && !isTons">
            <Icon
            :small="false"
            :icon="'mdi-pine-tree-variant'"
            iconColor="primary"
            :tooltipText="$t('showProductsView')"
            @icon-clicked="toggleVolumeView"
            />
          </v-col>
          <v-col cols="auto" v-if="!mobileCompatible && !depletes && isTons && tableView">
            <Icon
            :small="false"
            icon='mdi-leaf-maple'
            iconColor="primary"
            elementId="tract-harvest-toggle-volume"
            :tooltipText="$t('showSpeciesView')"
            @icon-clicked="toggleVolumeView"
            />
          </v-col>
          <v-col cols="auto" v-if="!mobileCompatible">
            <v-menu left>
              <template #activator="{ on }">
                <v-icon v-on="on" color="primary">mdi-dots-horizontal</v-icon>
              </template>
              <v-list id="tract-harvest-context-menu">
                <v-list-item
                v-for="(item, i) in contextMenuOptions"
                :id="kebabCase(item.label)"
                :key="`context-menu-${i}`"
                @click="item.action"
                :dataTestId="item.dataTestId">
                  <v-avatar left>
                    <v-icon color="black">{{item.icon}}</v-icon>
                  </v-avatar>
                  <v-list-item-title class="subtitle-1">{{ $t(item.label) }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>
          <v-col cols="auto">
            <Icon
            icon="mdi-refresh"
            @icon-clicked="refreshTractHarvest"
            :tooltipText="$t('refresh')"
            :small="false"
            />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="tableView">
      <v-col cols="12" :class="mobileCompatible ? 'pa-0 mt-n4' : ''">
        <DataTable
        v-if="tractAnalysis"
        :items.sync="tractHarvest"
        :headers.sync="headers"
        :sumHeaders="sumHeaders"
        hideHeader
        :loading="loading"
        :showSearch="false"
        showTotals
        :groupBy="isTons ? 'woodType' : undefined"
        :abbreviateHeaders="mobileCompatible"
        >
          <template #group.header="{ group }">
            <span class="title">{{ isTons ? woodTypeName(group) : '' }}</span>
          </template>
          <template #group-total.product="{ group }">
            {{ `${isTons ? woodTypeName(group.group) : ''} Totals` }}
          </template>
          <template #total.product>
          {{ $t('totals') }}
          </template>
          <template #total.species>
            {{ $t('totals') }}
          </template>
          <template #group.summary.prepend="{ group }">
            <p v-if="$vuetify.breakpoint.xs" class="text-subtitle-2 ml-4 mt-4 mb-2 font-weight-black"> {{isTons ? woodTypeName(group) : ''}} Totals </p>
          </template>
        </DataTable>
      </v-col>
      <v-col cols="12" class="pt-0 mt-n2">
        <v-row justify="end" dense>
          <v-col cols="12" sm="6" lg="auto" :style="{width: `${Math.min(394, windowWidth)}px` }" v-if="depletes">
            <v-card outlined class="fill-height">
              <v-card-title class="pb-2">
                <span class="subtitle-1" style="color: #00000099">{{ $t('costTotals') }}</span>
              </v-card-title>
              <v-card-text>
                <v-simple-table>
                  <template v-for="(section, i) in depletionRows">
                    <tr :key="`analysistotal-spacer-${i}`" v-if="i > 0"><td class="pt-6"/><td/></tr>
                    <tr v-for="totalRow in section" :key="`analysis-totals-${totalRow.label}`" :style="`border: ${totalRow.topBorder ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'}`">
                      <td :class="totalRow.keyRight ? '' : 'font-weight-bold'" :style="totalRow.topBorder ? 'border-top: 1px solid rgba(0, 0, 0, 0.12);' : ''">{{$t(totalRow.label)}}</td>
                      <td :style="`text-align: right; ${totalRow.topBorder ? 'border-top: 1px solid rgba(0, 0, 0, 0.12);' : '' }`">{{formatMoney(totalRow.value)}}</td>
                    </tr>
                  </template>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" lg="auto" :style="{width: `${Math.min(394, windowWidth)}px` }">
            <v-card outlined class="fill-height">
              <v-card-title class="pb-2" v-if="tractTypeCategory === TractTypeCategory.Stumpage">
                <span class="subtitle-1" style="color: #00000099">{{ $t('profitAndLoss') }}</span>
              </v-card-title>
              <v-card-text>
                <v-simple-table>
                  <template v-for="(section, i) in totalRows">
                    <tr :key="`analysistotal-spacer-${i}`" v-if="i > 0"><td class="pt-6"/><td/></tr>
                    <tr v-for="totalRow in section" :key="`analysis-totals-${totalRow.label}`" :style="`border: ${totalRow.topBorder ? '1px solid rgba(0, 0, 0, 0.12)' : 'none'}`">
                      <td :class="totalRow.keyRight ? '' : 'font-weight-bold'" :style="totalRow.topBorder ? 'border-top: 1px solid rgba(0, 0, 0, 0.12);' : ''">{{$t(totalRow.label)}}</td>
                      <td :style="`text-align: right; ${totalRow.topBorder ? 'border-top: 1px solid rgba(0, 0, 0, 0.12);' : '' }`">{{formatMoney(totalRow.value)}}</td>
                    </tr>
                  </template>
                </v-simple-table>
                <v-tooltip color="primary" left v-if="depletes && tractAnalysis?.balance < 0">
                  <template #activator="{ on }">
                    <v-container v-on="on" class="pa-0 mb-n6" id="show-overcut-depletion-in-profit">
                      <v-checkbox
                      v-model="showOvercutDepletionInProfit"
                      class="mt-1"
                      :label="$t('showOvercutDepletionInProfit')"
                      />
                    </v-container>
                  </template>
                  <span class="subtitle-1 white--text">{{ $t(showOvercutDepletionInProfit ? 'excludeOvercutTooltip' : 'includeOvercutTooltip') }}</span>
                </v-tooltip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" class="pt-0">
        <v-row justify="end" no-gutters>
          <span class="font-italic">{{$t('tractHarvestTicketStatusDenotation')}}</span>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-show="!tableView" dense>
      <v-col cols="12" sm="6" lg="4">
        <canvas id="prod-chart"/>
      </v-col>
      <v-col cols="12" sm="6" lg="4">
        <canvas id="stumpage-chart"/>
      </v-col>
      <v-col cols="12" sm="6" lg="4">
        <canvas id="loads-chart"/>
      </v-col>
    </v-row>
    <v-row v-show="!tableView" class="mt-12" dense justify="center">
      <v-col cols="12">
        <canvas id="harvested-chart" style="min-height: 350px"/>
      </v-col>
    </v-row>
    <v-dialog v-model="includePlInPdfDialog" width="400">
      <ConfirmDialog
      :title="$t('downloadOptions')"
      :confirmText=" $t('downloadPDF')"
      color="primary"
      body=""
      @confirm="downloadPDF"
      @cancel="includePlInPdfClose"
      >
      <template #custom-body>
        <v-checkbox
        v-model="includePlInPdf"
        color="primary"
        :label="$t('includeTractSummaryBody')"></v-checkbox>
      </template>
      </ConfirmDialog>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { Chart } from 'chart.js'
import { saveAs } from 'file-saver'
import { tonStringFromPounds, formatMoney, numberWithCommas, poundsToTons } from '@/utils/NumericMutations.js'
import { generateCruiseAnalysisReport } from '@/utils/pdf-formatters/CruiseAnalysisReport.js'
import { tractHarvestHeaders, tractDepletionHarvestHeaders } from '@/headers/Tract.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { TractTypeCategory, WoodType, HarvestUnits, TractTypeSpecialization } from '@/utils/Enumerations'
import { generateCsvString } from '@/utils/CSVUtility'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { getChartColors, getChartColor } from '@/components/dashboard/ChartColors.js'
import { kebabCase } from 'lodash'

export default {
  name: 'TractHarvest',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    TabbedColumnAdjuster: () => import('@/components/helper/TabbedColumnAdjuster.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    asOfDate: {
      type: String,
      default: null
    },
    shouldRefresh: {
      type: Boolean,
      default: false
    },
    mobileCompatible: {
      type: Boolean,
      default: false
    },
    distanceFromTract: {
      type: String,
      required: false,
      default: undefined
    }
  },

  data: () => ({
    HarvestUnits,
    TractTypeCategory,
    tractAnalysis: undefined,
    tableView: true,
    units: HarvestUnits.forInt(parseInt(localStorage.getItem(LocalStorageKeys.TRACT_CRUISE_UNITS))) ?? HarvestUnits.Tons,
    loadsChart: undefined,
    productionCostsChart: undefined,
    cruiseValueChart: undefined,
    harvestedChart: undefined,
    loading: false,
    dateFilterCleared: false,
    distanceCleared: false,
    columnSet: null,
    includePlInPdfDialog: false,
    includePlInPdf: localStorage.getItem(LocalStorageKeys.INCLUDE_TRACT_SUMMARY_PDF),
    columnAdjusterOpen: false,
    newColumnSet: null,
    showOvercutDepletionInProfit: true
  }),

  computed: {
    harvestHeaders () {
      const isUnit = !!this.tract.tractGroupId
      return this.depletes ? tractDepletionHarvestHeaders(this.tractTypeCategory, this.units, isUnit)
        : tractHarvestHeaders(this.tractTypeCategory, this.units, this.tract.type?.specialization, isUnit)
    },
    columnAdjusterTabs () {
      return ['cruise', 'operational', 'financial']
    },
    tractHarvest () {
      if (this.isTons) {
        return this.tractAnalysis?.tractHarvestAnalysis ?? []
      }

      const volumeData = this.tractAnalysis?.tractVolumeHarvestAnalysis ?? []
      const isObligationUncapped = this.tract.type?.specialization === TractTypeSpecialization.ObligationUncapped.value
      const isObligation = isObligationUncapped ||
        this.tract.type?.specialization === TractTypeSpecialization.ObligationCapped.value
      if (!isObligation) {
        return volumeData
      }
      return volumeData.map(item => ({
        ...item,
        ...(isObligationUncapped ? {
          totalObligation: (item.obligationRate ?? 0) * (item.expectedMbf ?? 0),
          obligationBalance: ((item.obligationRate ?? 0) * (item.expectedMbf ?? 0)) - (item.obligationAccrued ?? 0)
        } : {}),
        obligationAccrued: item.obligationAccrued ?? 0
      }))
    },
    totals () {
      return this.tractHarvest.reduce((totals, th) => {
        totals.harvestedTons += th.harvestedTons
        totals.totalCost += th.totalCost
        totals.totalTractPayments = th.totalTractPayments
        totals.total = totals.totalCost + totals.totalTractPayments
        return totals
      }, {
        harvestedTons: 0,
        totalCost: 0,
        totalTractPayments: 0,
        total: 0
      })
    },

    tractTypeCategory () {
      const t = TractTypeCategory.forInt(this.tract.type.category)
      return t
    },

    sumHeaders () {
      return (this.columnSet) ? this.columnSet.getSummableHeaders() : []
    },

    totalRows () {
      if (!this.tractAnalysis) return []
      if (this.tractTypeCategory === TractTypeCategory.Delivered) return [[{ label: 'tractPayments', value: this.tractAnalysis.totalTractPayments }]]
      const key = (string) => string.concat(this.effectiveShowOvercut ? '' : 'NoOvercut')
      return [
        [
          { label: 'revenue', value: this.tractAnalysis.totalRevenue },
          { label: 'stumpageCost', value: this.tractAnalysis[key('totalStumpageCost')], keyRight: true },
          { label: 'productionCost', value: this.tractAnalysis.totalProductionCost, keyRight: true },
          { label: 'totalCost', value: this.tractAnalysis[key('totalCost')] },
          { label: 'grossProfit', value: this.tractAnalysis[key('totalGrossProfit')] },
          { label: 'tractPayments', value: this.tractAnalysis.totalTractPayments },
          { label: 'tractPL', value: this.tractAnalysis[key('tractProfitLoss')], topBorder: true }
        ],
        [
          { label: 'purchaseCost', value: this.tractAnalysis.purchaseCost },
          { label: 'contractStumpageCost', value: this.tractAnalysis.totalStumpageCost, keyRight: true },
          { label: 'balance', value: this.tractAnalysis.balance }
        ]
      ].slice(0, 1)
    },

    depletionRows () {
      if (!this.tractAnalysis || !this.depletes) return []
      return [
        [
          { label: 'totalCost', value: this.tractAnalysis.purchaseCost },
          { label: 'totalDepletion', value: this.tractAnalysis.purchaseCost - this.tractAnalysis.depletionBalance, keyRight: true },
          { label: 'remainingCost', value: Math.max(this.tractAnalysis.depletionBalance, 0), topBorder: true },
          { label: 'depletionBalance', value: this.tractAnalysis.depletionBalance }
        ]
      ]
    },

    headers () {
      if (!this.columnSet) return []
      return this.columnSet.getColumns()
    },

    contextMenuOptions () {
      return [
        { label: 'downloadPDF', icon: 'mdi-file-pdf-box', dataTestId: 'tract-harvest-pdf-download', action: this.includePlInPdfOpen },
        { label: 'downloadCSV', icon: 'mdi-file-delimited-outline', dataTestId: 'tract-harvest-download', action: this.downloadCSV }
      ]
    },

    mobileMenuOptions () {
      return {
        tab: this.$t('tractCostAnalysis'),
        actions: [
          {
            id: 'refresh',
            action: this.refreshTractHarvest
          },
          {
            icon: 'mdi-application-edit',
            tooltipText: this.$t('adjustColumns'),
            action: this.openColumnAdjuster,
            divider: true
          },
          !this.depletes && !this.isTons ? {
            icon: 'mdi-pine-tree-variant',
            tooltipText: this.$t('showProductsView'),
            action: this.toggleVolumeView
          } : undefined,
          !this.depletes && this.isTons && this.tableView ? {
            icon: 'mdi-leaf-maple',
            tooltipText: this.$t('showSpeciesView'),
            action: this.toggleVolumeView
          } : undefined,
          this.isTons ? {
            icon: this.tableView ? 'mdi-graph' : 'mdi-table',
            tooltipText: this.tableView ? this.$t('viewCharts') : this.$t('viewTable'),
            action: this.toggleTableView
          } : undefined,
          ...this.contextMenuOptions.map(({ label, icon, action }) => ({
            icon,
            tooltipText: this.$t(label),
            action
          }))
        ].filter(item => item !== undefined)
      }
    },

    isTons () {
      return this.units === HarvestUnits.Tons
    },

    depletes () {
      return this.tract.type.specialization === TractTypeSpecialization.Depletion.value
    },

    effectiveShowOvercut () {
      const checkboxVisible = this.depletes && this.tractAnalysis?.balance < 0
      return !checkboxVisible || this.showOvercutDepletionInProfit
    },

    windowWidth () {
      return window.innerWidth
    }
  },

  watch: {
    shouldRefresh (val) {
      if (val) {
        this.refreshTractHarvest()
      }
    },

    units () {
      this.columnSet = this.harvestHeaders
      this.setupCharts()
    },

    showOvercutDepletionInProfit () {
      this.columnSet = this.harvestHeaders
      this.createHarvestedChart()
    }
  },

  created () {
    this.columnSet = this.harvestHeaders
    this.includePlInPdf = localStorage.getItem(LocalStorageKeys.INCLUDE_TRACT_SUMMARY_PDF) === 'true'
    this.showOvercutDepletionInProfit = localStorage.getItem(LocalStorageKeys.SHOW_OVERCUT_DEPLETION_IN_PROFIT) !== 'false'
    this.$emit('extra-menu-options', this.mobileMenuOptions)
  },

  mounted () {
    this.refreshTractHarvest()
  },

  beforeDestroy () {
    localStorage.setItem(LocalStorageKeys.SHOW_OVERCUT_DEPLETION_IN_PROFIT, this.showOvercutDepletionInProfit)
  },

  methods: {
    ...mapActions('tract', ['getTractHarvest', 'fetchTractLandowners']),
    ...mapActions('notes', ['fetchTractNotes']),
    tonStringFromPounds,
    formatMoney,
    numberWithCommas,
    generateCruiseAnalysisReport,
    utcToLocalDate,
    kebabCase,

    woodTypeName (e) {
      return WoodType.forInt(e).name
    },

    async refreshTractHarvest () {
      if (!this.loading) {
        this.loading = true
        this.tractAnalysis = await this.getTractHarvest({
          tractId: this.tract.tractId,
          asOfDate: this.dateFilterCleared ? null : this.asOfDate
        })
        this.loading = false
        this.setupCharts()
      }
    },

    includePlInPdfOpen () {
      this.includePlInPdfDialog = true
    },

    includePlInPdfClose () {
      this.includePlInPdfDialog = false
    },

    async downloadPDF () {
      const tractNotes = await this.fetchTractNotes(this.tract.tractId)
      const landowners = await this.fetchTractLandowners(this.tract.tractId)
      this.generateCruiseAnalysisReport({
        tractAnalysis: this.tractAnalysis,
        tract: this.tract,
        tractNotes: tractNotes,
        headers: this.headers,
        sumHeaders: this.sumHeaders,
        includeTotalsTable: this.includePlInPdf,
        includeCostTotalsTable: this.includePlInPdf && this.depletes,
        includeOvercut: this.effectiveShowOvercut,
        landowners: landowners,
        weightMode: this.isTons
      })
      localStorage.setItem(LocalStorageKeys.INCLUDE_TRACT_SUMMARY_PDF, this.includePlInPdf)
      this.includePlInPdfDialog = false
    },

    clearDateFilter () {
      this.dateFilterCleared = true
      this.refreshTractHarvest()
    },

    clearDistance () {
      this.distanceCleared = true
    },

    isNoData (data) {
      return data.length === 0 || data.every(i => i === 0)
    },

    setupCharts () {
      this.createLoadsChart()
      this.createHarvestedChart()
      this.createProdCostsChart()
      this.createRemainingCruiseValueChart()
    },

    createHarvestedChart () {
      if (this.harvestedChart) this.harvestedChart.destroy()
      const cvx = document.getElementById('harvested-chart')
      this.harvestedChart = new Chart(cvx, this.getHarvestedChartConfig())
    },

    shouldShowTotalColumn (columnId) {
      return !this.columnSet.isColumnHidden(columnId)
    },

    getChartTitle (text) {
      return {
        font: {
          family: 'Roboto, sans-serif',
          size: 16
        },
        display: true,
        text: text
      }
    },

    getChartSubtitle (isDisplayed) {
      return {
        display: isDisplayed,
        text: this.$t('noDataAvailableForChart'),
        color: 'black',
        font: {
          size: 16,
          family: 'Roboto, sans-serif',
          weight: 400
        },
        padding: {
          top: 20
        }
      }
    },

    createLoadsChart () {
      if (this.loadsChart) this.loadsChart.destroy()
      const data = this.tractHarvest.map(th => th.loads)
      const labels = !this.isNoData(data) ? this.tractHarvest.map(th => th.product) : []
      const dataSet = {
        labels: labels,
        datasets: [{
          data: data,
          hoverOffset: 4,
          backgroundColor: getChartColors(labels.length)
        }]
      }

      const config = {
        type: 'pie',
        data: dataSet,
        options: {
          plugins: {
            title: this.getChartTitle(this.$t('loadsByProduct')),
            subtitle: this.getChartSubtitle(this.isNoData(data))
          }
        }
      }

      const cvx = document.getElementById('loads-chart')
      this.loadsChart = new Chart(cvx, config)
    },

    getCutoutPercentage ({ harvestedTons, expectedTons }) {
      if (expectedTons === 0) return this.$t('notAvailable')

      const cutoutPercentage = (harvestedTons / expectedTons) * 100

      if (cutoutPercentage > 100) return '100.00%'

      return `${cutoutPercentage.toFixed(2)}%`
    },

    createProdCostsChart () {
      if (this.productionCostsChart) this.productionCostsChart.destroy()
      const data = this.tractHarvest.map(th => th.totalProductionCost)
      const labels = !this.isNoData(data) ? this.tractHarvest.map(th => th.product) : []
      const dataSet = {
        labels: labels,
        datasets: [{
          data: data,
          hoverOffset: 4,
          backgroundColor: getChartColors(labels.length)
        }]
      }

      const config = {
        type: 'pie',
        data: dataSet,
        options: {
          plugins: {
            title: this.getChartTitle(this.$t('productionCostByProduct')),
            subtitle: this.getChartSubtitle(this.isNoData(data))
          }
        }
      }

      const cvx = document.getElementById('prod-chart')

      this.productionCostsChart = new Chart(cvx, config)
    },

    createRemainingCruiseValueChart () {
      if (this.cruiseValueChart) this.cruiseValueChart.destroy()
      const data = this.tractHarvest.map(th => th.remainingCruiseValue)
      const labels = !this.isNoData(data) ? this.tractHarvest.map(th => th.product) : []
      const dataSet = {
        labels: labels,
        datasets: [{
          data: data,
          hoverOffset: 4,
          backgroundColor: getChartColors(labels.length)
        }]
      }

      const config = {
        type: 'pie',
        data: dataSet,
        options: {
          plugins: {
            title: this.getChartTitle(this.$t('remainingCruiseValueByProduct')),
            subtitle: this.getChartSubtitle(this.isNoData(data))
          }
        }
      }

      const cvx = document.getElementById('stumpage-chart')
      this.cruiseValueChart = new Chart(cvx, config)
    },

    newColumnsSelected (columnSet) {
      this.columnSet = columnSet
    },

    getHarvestedData (tractHarvest) {
      if (!tractHarvest) {
        return
      }

      const labels = tractHarvest.map(cd => cd.product)
      const harvestedData = tractHarvest.map(cd => this.isTons ? poundsToTons(cd.harvestedLBS) : cd.harvestedMBF)
      const expectedData = tractHarvest.map(cd => this.isTons ? poundsToTons(cd.expectedLBS) : cd.expectedMBF)

      const dataSets = [
        {
          label: this.isTons ? this.$t('harvestedTons') : this.$t('harvestedMBF'),
          data: harvestedData
        },
        {
          label: this.isTons ? this.$t('expectedTons') : this.$t('expectedMBF'),
          data: expectedData
        }
      ].map((item, index, array) => ({
        ...item,
        backgroundColor: getChartColor(index, array.length)
      }))

      return {
        labels: labels,
        datasets: dataSets
      }
    },

    getHarvestedChartConfig () {
      const noData = this.getHarvestedData(this.tractHarvest).datasets?.every(ds => this.isNoData(ds.data))
      return {
        type: 'bar',
        data: noData ? [] : this.getHarvestedData(this.tractHarvest),
        options: {
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              font: {
                family: 'Roboto, sans-serif',
                size: 16
              },
              display: true,
              text: this.isTons ? this.$t('harvestedVersusExpectedTons') : this.$t('harvestedVersusExpectedMBF')
            },
            subtitle: this.getChartSubtitle(noData)
          },
          labels: {
            defaultFontSize: 16
          }
        }
      }
    },

    generateCsvString,

    downloadCSV () {
      const csvString = this.generateCsvString(this.tractHarvest, [
        this.isTons ? { label: 'woodType', value: th => WoodType.forInt(th.woodType).name } : undefined,
        ...this.columnSet.columns.filter(c => !!c.text).map(c => ({
          text: c.text,
          value: i => i[c.value]
        }))
      ].filter(item => item !== undefined))
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      saveAs(blob, `Cruise_Analysis_${this.tract.name.replaceAll(' ', '_')}_${moment().format('MM_DD_YY')}.csv`)
    },

    toggleTableView () {
      this.tableView = !this.tableView
      this.$emit('extra-menu-options', this.mobileMenuOptions)
    },

    toggleVolumeView () {
      switch (this.units) {
        case HarvestUnits.Tons: this.units = HarvestUnits.MBF; break
        default: this.units = HarvestUnits.Tons
      }
      this.$emit('extra-menu-options', this.mobileMenuOptions)
      localStorage.setItem(LocalStorageKeys.TRACT_CRUISE_UNITS, this.units.value)
    },

    openColumnAdjuster () {
      this.columnAdjusterOpen = true
    }
  }
}
</script>
